import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  ArrowLeft, 
  ArrowUpRight,
  AlertCircle,
  CheckCircle2,
  Wallet,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../../utils/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Withdraw() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Check if account is pending
  const isPending = user?.status === 'pending';

  // If account is pending, show restriction message
  if (isPending) {
    return (
      <div className="max-w-lg mx-auto" data-testid="withdraw-restricted">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          {t('common.back')}
        </button>

        <div className="glass-card p-8 text-center">
          <div className="w-20 h-20 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Clock className="w-10 h-10 text-amber-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">{t('withdrawals.accountUnderReview')}</h2>
          <p className="text-slate-400 mb-6">
            {t('withdrawals.withdrawalsAvailableAfterApproval')}
          </p>
          <Button
            onClick={() => navigate('/dashboard')}
            className="bg-blue-500 hover:bg-blue-600 text-white rounded-xl px-8"
          >
            {t('withdrawals.backToDashboard')}
          </Button>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const amountValue = parseFloat(amount);
    
    if (!amount || amountValue <= 0) {
      toast.error(t('withdrawals.enterValidAmount'));
      return;
    }
    
    if (amountValue < 50) {
      toast.error(t('withdrawals.minimumWithdrawal'));
      return;
    }
    
    if (amountValue > (user?.balance || 0)) {
      toast.error(t('withdrawals.insufficientBalance'));
      return;
    }
    
    if (!user?.iban && !user?.revolut_account) {
      toast.error(t('withdrawals.configureIbanFirst'));
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/withdrawals`, {
        amount: amountValue
      });
      setSuccess(true);
      toast.success(t('withdrawals.requestSubmitted'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-lg mx-auto animate-fade-in" data-testid="withdraw-success">
        <div className="glass-card p-8 text-center">
          <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-emerald-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2 font-['Outfit']">
            {t('withdrawals.requestSent')}
          </h2>
          <p className="text-slate-400 mb-6">
            {t('withdrawals.requestReceived')} <span className="text-white font-semibold">€{parseFloat(amount).toLocaleString('pt-PT', { minimumFractionDigits: 2 })}</span> {t('withdrawals.wasReceived')}
          </p>
          <div className="flex gap-3 justify-center">
            <Button
              onClick={() => navigate('/dashboard')}
              className="bg-blue-500 hover:bg-blue-600 rounded-xl"
            >
              {t('withdrawals.backToDashboard')}
            </Button>
            <Button
              onClick={() => navigate('/transactions')}
              variant="outline"
              className="bg-white/5 hover:bg-white/10 border-white/10 rounded-xl"
            >
              {t('withdrawals.viewTransactions')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto animate-fade-in" data-testid="withdraw-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-slate-400" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white font-['Outfit']">{t('withdrawals.requestWithdrawal')}</h1>
          <p className="text-slate-400 text-sm">{t('withdrawals.withdrawFunds')}</p>
        </div>
      </div>

      {/* Available Balance Card */}
      <div className="glass-card p-6 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-blue-500/10 flex items-center justify-center">
            <Wallet className="w-7 h-7 text-blue-400" />
          </div>
          <div>
            <p className="text-slate-400 text-sm">{t('withdrawals.availableBalanceLabel')}</p>
            <p className="text-3xl font-bold text-white font-['Outfit']">
              {formatCurrency(user?.balance)}
            </p>
          </div>
        </div>
      </div>

      {/* Payment Method Warning */}
      {!user?.iban && !user?.revolut_account && (
        <div className="flex items-start gap-3 p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl mb-6">
          <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-amber-400 font-medium">{t('withdrawals.paymentMethod')}</p>
            <p className="text-sm text-slate-400 mt-1">
              {t('withdrawals.configureIbanFirst')}
            </p>
            <Link 
              to="/profile" 
              className="inline-flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300 mt-2 font-medium"
            >
              {t('common.configure')} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Amount Input */}
        <div className="glass-card p-6">
          <Label className="text-slate-300 mb-3 block">{t('withdrawals.amountToWithdraw')}</Label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl text-slate-400">€</span>
            <Input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              min="0"
              max={user?.balance || 0}
              step="0.01"
              className="pl-12 h-16 text-2xl bg-[#161C22] border-white/10 text-white rounded-xl focus:border-blue-500"
              required
              data-testid="withdraw-amount"
            />
          </div>
          
          {/* Quick Amount Buttons */}
          <div className="flex gap-2 mt-4">
            {[25, 50, 75, 100].map((percentage) => {
              const quickAmount = ((user?.balance || 0) * percentage / 100).toFixed(2);
              return (
                <button
                  key={percentage}
                  type="button"
                  onClick={() => setAmount(quickAmount)}
                  className="flex-1 py-2 px-3 bg-white/5 hover:bg-white/10 rounded-lg text-sm text-slate-300 transition-colors"
                >
                  {percentage}%
                </button>
              );
            })}
          </div>
        </div>

        {/* Transfer Info */}
        {(user?.iban || user?.revolut_account) && (
          <div className="glass-card p-6">
            <div className="flex items-center gap-2 text-slate-400 mb-4">
              <ArrowUpRight className="w-5 h-5" />
              <span className="font-medium">{t('withdrawals.transferTo')}</span>
            </div>
            <div className="space-y-3">
              {user?.iban && (
                <div className="p-4 bg-white/5 rounded-xl">
                  <p className="text-sm text-slate-400">IBAN {user?.bank_name && `(${user.bank_name})`}</p>
                  <p className="text-lg text-white font-mono">{user.iban}</p>
                </div>
              )}
              {user?.revolut_account && (
                <div className="p-4 bg-purple-500/10 border border-purple-500/20 rounded-xl">
                  <p className="text-sm text-purple-400">Revolut</p>
                  <p className="text-lg text-white">{user.revolut_account}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={loading || !amount || parseFloat(amount) <= 0 || parseFloat(amount) > (user?.balance || 0)}
          className="w-full h-14 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-semibold text-lg shadow-[0_0_20px_rgba(59,130,246,0.3)] transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="withdraw-submit"
        >
          {loading ? (
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              {t('withdrawals.processing')}
            </div>
          ) : (
            t('withdrawals.requestWithdrawal')
          )}
        </Button>

        <p className="text-sm text-slate-500 text-center">
          {t('withdrawals.minimumInfo')} <span className="text-white font-medium">€50,00</span>.<br/>
          {t('withdrawals.processingInfo')} <span className="text-white font-medium">5 {t('withdrawals.businessDays')}</span> {t('withdrawals.afterApproval')}.
        </p>
      </form>
    </div>
  );
}
